import { previewMode } from 'utils/dynamicData';
import { mapBanner } from 'utils/cms/utils';

function mapBg(cmsData) {
	const onesheetUrl = cmsData?.pages?.homeEnt?.onesheet?.custom ? cmsData.pages.homeEnt.onesheet.url : cmsData?.onesheet?.url;
	const headerImageUrl = cmsData?.pages?.homeEnt?.headerImage?.custom ? cmsData.pages.homeEnt.headerImage.url : cmsData?.headerImage?.url;

	return {
		desktop: { asset: onesheetUrl },
		mobile: { asset: headerImageUrl },
		tablet: { asset: headerImageUrl },
		tabletLandscape: { asset: headerImageUrl },
	};
}

export function mapCustomiseRegion(cmsData) {
	const sections = cmsData?.pages?.homeEnt?.sections;
	if (!sections) return null;

	const filterOverrides = cmsData?.pages?.homeEnt?.filters || {};

	return Object.entries(sections).reduce((acc, [key, section]) => {
		const filterTitle = filterOverrides[key]?.title || section.title;
		acc[key] = {
			order: section.order,
			title: section.title,
			shortTitle: filterTitle,
			filterTitle, // Added to allow easier definition of availabilities list
		};

		// If showDisclaimer is true, include the disclaimer text (or default if not provided)
		if (section.showDisclaimer) {
			acc[key].disclaimer = section.disclaimer || "A digital purchase provides a limited license to access the content. See the retailer's terms for details.";
		}

		return acc;
	}, {});
}

function mapMerchants(cmsData) {
	const homeEntData = cmsData?.pages?.homeEnt;
	if (!homeEntData?.sections) return null;

	return Object.entries(homeEntData.sections).reduce((addProductsMultiRegion, [sectionKey, section]) => {
		addProductsMultiRegion[sectionKey] = {
			digital: Object.entries(section.items).reduce((digital, [itemKey, item]) => {
				const { customMerchant, link, merchant, order, title, useCustomMerchant } = item;

				if (useCustomMerchant ? !customMerchant?.image : !merchant) return digital; // Skip if required merchant info is missing

				const isCustomMerchant = useCustomMerchant && customMerchant;
				const merchantId = (!isCustomMerchant && merchant) || itemKey; // Use merchant ID if applicable, otherwise use itemKey

				const sectionFilterTitle = homeEntData.filters?.[sectionKey]?.title || section.title; // Get the filter title or fallback to section title

				digital[merchantId] = {
					order,
					title,
					[sectionFilterTitle]: {
						[merchantId]: { link }
					}
				};
				return digital;
			}, {}),
		};
		return addProductsMultiRegion;
	}, {});
}

function mapCustomMerchants(cmsData) {
	const homeEntData = cmsData?.pages?.homeEnt;
	if (!homeEntData?.sections) return null;

	return Object.values(homeEntData.sections).reduce((merchants, section) => {
		Object.entries(section.items).forEach(([itemKey, item]) => {
			if (item.useCustomMerchant && item.customMerchant) {
				merchants[itemKey] = {
					logo: item.customMerchant.image,
					logoAltText: item.customMerchant.alt,
					name: item.customMerchant.name,
				};
			}
		});
		return merchants;
	}, {});
}

export default function mapHomeEnt(cmsData, appData) {
	if (!appData?.pages?.boltHomeEnt) return null;

	const homeEntData = cmsData?.pages?.homeEnt;
	// Allow preview mode for non-active pages, but only if that is the page being previewed (directly accessed by URL)
	const showInactive = previewMode && location.pathname.endsWith('/watch-at-home/');
	if (!homeEntData?.active && !showInactive) return null;

	const isDefault = homeEntData?.settings?.default ?? false;

	return {
		disabled: false,
		type: 'homeEnt',
		data: {
			addProductsMultiRegion: mapMerchants(cmsData),
			bg: mapBg(cmsData),
			customiseRegion: mapCustomiseRegion(cmsData),
			notificationBanner: mapBanner(homeEntData?.banner),
			overrideMerchantDetails: mapCustomMerchants(cmsData),
		},
		isMainPage: isDefault,
		path: isDefault ? '/' : undefined, // Only set path to '/' if this is the default page, otherwise let Bolt determine the path based on the page key (e.g. '/homeEnt')
		// When this page is the default, keep the original path so nav links and URL detection still resolve to it
		...(isDefault ? { additionalPaths: '/watch-at-home/', alternatePaths: '/watch-at-home/' } : {}),
	};
}